import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { AdminPanelSettings, Close, Lock, Refresh, Search } from '@mui/icons-material';
import { AdminEvent, AdminSummary, AdminUser, getAdminBoardScans, getAdminSummary, getAdminTimeline } from '../services/api';
import { AdminBoardScanner } from '../components/AdminBoardScanner';
import { SegmentedToggle } from '../components/SegmentedToggle';
import { useChartTokens } from '../components/charts/chartKit';
import { useIsAdmin } from '../components/useIsAdmin';

// The read-only admin page: who's using the site, what they do and what it costs (docs/admin-portal-plan.md,
// "Revised start"). Every call goes through the backend's read:admin check; this page is only a view.

type Range = '7' | '30' | '90';
type AdminTab = 'overview' | 'users' | 'costs' | 'activity' | 'scanner';

const money = (v: number) => (v === 0 ? '$0' : v < 0.01 ? '<$0.01' : `$${v.toFixed(2)}`);
const gb = (b: number) => (b >= 1 << 30 ? `${(b / (1 << 30)).toFixed(1)} GB` : `${Math.round(b / (1 << 20))} MB`);
const secs = (ms: number) => (ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${Math.round(ms)} ms`);
const ago = (iso: string) => {
  const m = (Date.now() - new Date(iso).getTime()) / 60000;
  if (m < 1) return 'just now';
  if (m < 60) return `${Math.round(m)} min ago`;
  if (m < 60 * 24) return `${Math.round(m / 60)} h ago`;
  const d = Math.round(m / 1440);
  return `${d} day${d === 1 ? '' : 's'} ago`;
};
const shortHash = (h: string) => `user ${h.slice(0, 6)}`;

const ROUTES: [RegExp, string][] = [
  [/^\/api\/sim\/run$/, 'Simulations (run)'],
  [/^\/api\/sim\/momentum$/, 'Momentum'],
  [/^\/api\/sim\/matchup$/, 'Matchups'],
  [/^\/api\/sim\/parlay$/, 'Parlays'],
  [/^\/api\/sim\/slip$/, 'Slip Builder checks'],
  [/^\/api\/sim\/board-scan\/matchups$/, 'Board scanner matchups'],
  [/^\/api\/sim\/board-scan$/, 'Board scanner'],
  [/^\/api\/sim\/board-scans/, 'Board scan history'],
  [/^\/api\/sim\/explain$/, 'AI explanations'],
  [/^\/api\/sim\/matchup-explain$/, 'AI matchup summaries'],
  [/^\/api\/ai\/chat/, 'AI chat'],
  [/^\/api\/sim\/driver-races$/, 'Driver details (races)'],
  [/^\/api\/sim\/track-ranks/, 'Track-rank pills'],
  [/^\/api\/sim\/streaks$/, 'Streak pills'],
  [/^\/api\/sim\/uncertainty$/, 'Uncertainty bands'],
  [/^\/api\/sim\/model-check$/, 'Trend vs model'],
  [/^\/api\/sim\/similarity/, 'Track similarity'],
  [/^\/api\/sim\/runs/, 'Previous simulations'],
  [/^\/api\/slips/, 'Saved slips'],
  [/^\/api\/stats\//, 'Stats pages'],
  [/^\/api\/predict/, 'Predictions'],
  [/^\/api\/research/, 'Research'],
  [/^\/api\/account/, 'Account'],
  [/^\/api\/admin/, 'Admin page'],
];
const routeLabel = (r: string) => ROUTES.find(([re]) => re.test(r))?.[1] ?? r;

const TABS: Record<string, string> = { table: 'Table', tiers: 'Tiers', boom: 'Boom or bust' };
const PAGES: Record<string, string> = {
  '/': 'Home',
  '/simulator': 'Simulator',
  '/live': 'Race live',
  '/track-rankings': 'Track Rankings',
  '/track-similarity': 'Track similarity',
  '/research': 'Research',
  '/profile': 'Profile',
  '/admin': 'Admin',
};
/** A browser event as a readable feature name (actions grouped, e.g. every "simulate <track>" is one feature). */
const featureLabel = (name: string, detail: string) => {
  switch (name) {
    case 'page_view':
      return `Page · ${PAGES[detail] ?? (detail.startsWith('/research') ? 'Research' : detail)}`;
    case 'tab':
      return `Simulator · ${TABS[detail] ?? detail}`;
    case 'toggle':
      return `Setting · ${detail}`;
    case 'open':
      return `Opened · ${detail}`;
    case 'action':
      return detail.startsWith('simulate')
        ? 'Ran a simulation'
        : detail.startsWith('scan board')
          ? 'Scanned a board'
          : detail;
    default:
      return `${name} · ${detail}`;
  }
};
const eventLabel = (e: AdminEvent) =>
  e.kind === 'api'
    ? `${routeLabel(e.route ?? '')}${e.status && e.status >= 400 ? ` (error ${e.status})` : ''}`
    : e.name === 'action'
      ? actionLabel(e.detail ?? '')
      : featureLabel(e.name ?? '', e.detail ?? '');

/** "simulate Martinsville (wild)" -> "Ran a simulation · Martinsville (wild)"; "scan board (2 screenshots)" -> ... */
function actionLabel(detail: string): string {
  if (detail.startsWith('simulate ')) return `Ran a simulation · ${detail.slice('simulate '.length)}`;
  if (detail.startsWith('scan board'))
    return `Scanned a board${detail.slice('scan board'.length).replace(/^ \((.*)\)$/, ' · $1')}`;
  return detail;
}

const Tile: React.FC<{ label: string; value: string; note?: string }> = ({ label, value, note }) => (
  <Paper sx={{ flex: '1 1 160px', minWidth: 0, p: 2 }}>
    <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: 'text.secondary' }}>{label}</Typography>
    <Typography sx={{ fontSize: 26, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
    {note && <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{note}</Typography>}
  </Paper>
);

const Head: React.FC<{ cols: string[]; leftCols?: number }> = ({ cols, leftCols = 1 }) => (
  <TableHead>
    <TableRow>
      {cols.map((h, i) => (
        <TableCell
          key={h + i}
          align={i < leftCols ? 'left' : 'right'}
          sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary', whiteSpace: 'nowrap' }}
        >
          {h.toUpperCase()}
        </TableCell>
      ))}
    </TableRow>
  </TableHead>
);

/** Daily cost, stacked: BigQuery for users' requests, BigQuery not tied to a user (upkeep), Gemini. */
const CostChart: React.FC<{ daily: AdminSummary['daily'] }> = ({ daily }) => {
  const tok = useChartTokens();
  const W = 880;
  const H = 220;
  const L = 48;
  const B = 24;
  const max = Math.max(0.0001, ...daily.map((d) => d.bigQueryUSD + d.systemUSD + d.geminiUSD)) * 1.1;
  const bw = (W - L - 10) / Math.max(1, daily.length);
  const Y = (v: number) => H - B - (v / max) * (H - B - 10);
  const cols = { bigQueryUSD: tok.series[0], systemUSD: tok.series[6], geminiUSD: tok.series[2] };
  // Hovering a day highlights its bar (the others dim) and shows its numbers in a popover at once.
  const [hover, setHover] = useState<number | null>(null);
  const hd = hover !== null ? daily[hover] : null;
  const hx = hover !== null ? ((L + hover * bw + bw / 2) / W) * 100 : 0;
  return (
    <Box sx={{ position: 'relative' }} onMouseLeave={() => setHover(null)}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block' }}>
        {[0, 1, 2, 3, 4].map((g) => (
          <g key={g}>
            <line x1={L} x2={W} y1={Y((max / 4) * g)} y2={Y((max / 4) * g)} stroke={tok.grid} />
            <text x={L - 6} y={Y((max / 4) * g) + 4} textAnchor="end" fontSize={10} fill={tok.axisText}>
              {money((max / 4) * g)}
            </text>
          </g>
        ))}
        {daily.map((d, i) => {
          let y0 = H - B;
          return (
            <g
              key={d.date}
              onMouseEnter={() => setHover(i)}
              onClick={() => setHover((cur) => (cur === i ? null : i))}
              opacity={hover !== null && hover !== i ? 0.4 : 1}
              style={{ cursor: 'pointer', transition: 'opacity 120ms' }}
            >
              {/* the whole column is the hover target, so short bars are easy to hit */}
              <rect x={L + i * bw} y={0} width={bw} height={H - B} fill={hover === i ? tok.grid : 'transparent'} />
              {(['bigQueryUSD', 'systemUSD', 'geminiUSD'] as const).map((k) => {
                const h = (d[k] / max) * (H - B - 10);
                y0 -= h;
                return (
                  <rect
                    key={k}
                    x={L + i * bw + 2}
                    y={y0}
                    width={Math.max(1, bw - 4)}
                    height={Math.max(0, h - 1)}
                    rx={2}
                    fill={cols[k]}
                  />
                );
              })}
              {(daily.length <= 10 || i % Math.ceil(daily.length / 10) === 0) && (
                <text x={L + i * bw + bw / 2} y={H - 6} textAnchor="middle" fontSize={10} fill={tok.axisText}>
                  {d.date.slice(5).replace('-', '/')}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      {hd && (
        <Paper
          sx={{
            position: 'absolute',
            top: 4,
            left: `${hx}%`,
            transform: `translateX(${hx > 70 ? '-100%' : hx < 30 ? '0' : '-50%'})`,
            p: 1.25,
            minWidth: 210,
            pointerEvents: 'none',
            zIndex: 2,
            boxShadow: 6,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
            {new Date(`${hd.date}T12:00:00Z`).toLocaleDateString('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
            })}
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 0.75 }}>
            {hd.users} active user{hd.users === 1 ? '' : 's'}
          </Typography>
          {(
            [
              ['BigQuery (users)', hd.bigQueryUSD, cols.bigQueryUSD],
              ['BigQuery (upkeep)', hd.systemUSD, cols.systemUSD],
              ['Gemini', hd.geminiUSD, cols.geminiUSD],
            ] as const
          ).map(([l, v, c]) => (
            <Stack key={l} direction="row" spacing={1} alignItems="center" sx={{ fontSize: 12.5 }}>
              <Box sx={{ width: 9, height: 9, borderRadius: 0.5, bgcolor: c, flex: 'none' }} />
              <Box sx={{ flex: 1 }}>{l}</Box>
              <Box sx={{ fontVariantNumeric: 'tabular-nums' }}>{money(v)}</Box>
            </Stack>
          ))}
          <Stack
            direction="row"
            sx={{ fontSize: 12.5, fontWeight: 700, mt: 0.5, pt: 0.5, borderTop: 1, borderColor: 'divider' }}
          >
            <Box sx={{ flex: 1 }}>Total</Box>
            <Box sx={{ fontVariantNumeric: 'tabular-nums' }}>{money(hd.bigQueryUSD + hd.systemUSD + hd.geminiUSD)}</Box>
          </Stack>
        </Paper>
      )}
      <Stack direction="row" spacing={2} sx={{ fontSize: 12, color: 'text.secondary', pl: 6, flexWrap: 'wrap' }}>
        {(
          [
            ['BigQuery (users)', cols.bigQueryUSD],
            ['BigQuery (upkeep: background work and the studio)', cols.systemUSD],
            ['Gemini', cols.geminiUSD],
          ] as const
        ).map(([l, c]) => (
          <Stack key={l} direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: c }} />
            <span>{l}</span>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

const UserDrawer: React.FC<{ user: AdminUser | null; days: number; onClose: () => void }> = ({
  user,
  days,
  onClose,
}) => {
  const phone = useMediaQuery(useTheme().breakpoints.down('sm'));
  const q = useQuery({
    queryKey: ['adminTimeline', user?.hash, days],
    queryFn: () => getAdminTimeline(user!.hash, days),
    enabled: !!user,
    staleTime: 60_000,
  });
  return (
    <Drawer anchor="right" open={!!user} onClose={onClose} PaperProps={{ sx: { width: phone ? '100%' : 460, p: 2.5 } }}>
      {user && (
        <>
          <Stack direction="row" alignItems="center">
            <Typography sx={{ fontWeight: 800, fontSize: 18, flex: 1 }} noWrap>
              {user.email || shortHash(user.hash)}
            </Typography>
            <IconButton onClick={onClose} aria-label="Close">
              <Close />
            </IconButton>
          </Stack>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2 }}>
            {user.joined ? `Joined ${user.joined.slice(0, 10)} · ` : ''}
            {user.provider ? `${user.provider} sign-in · ` : ''}last seen {ago(user.lastSeen)}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Tile label="SIMULATIONS" value={String(user.simulations)} />
            <Tile label="SCANS" value={String(user.scans)} />
            <Tile label="COST" value={money(user.costUSD)} note={`${days} days`} />
          </Stack>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>Latest activity</Typography>
          {q.isLoading && <CircularProgress size={22} />}
          {q.error && <Alert severity="error">{(q.error as Error).message}</Alert>}
          {(q.data ?? []).map((e, i) => {
            const prev = q.data![i - 1];
            const newSession = !prev || (e.session_id && prev.session_id && e.session_id !== prev.session_id);
            const tokens = (e.gemini_in ?? 0) + (e.gemini_out ?? 0);
            return (
              <React.Fragment key={i}>
                {newSession && e.kind === 'rum' && i > 0 && (
                  <Box sx={{ my: 1, borderTop: 2, borderColor: 'divider' }} />
                )}
                <Stack direction="row" spacing={1.5} sx={{ py: 0.75, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography sx={{ width: 82, fontSize: 12, color: 'text.secondary', flex: 'none' }}>
                    {new Date(e.event_time).toLocaleString('en-US', {
                      month: 'numeric',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </Typography>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13 }}>{eventLabel(e)}</Typography>
                    <Typography sx={{ fontSize: 11.5, color: e.kind === 'api' ? 'warning.main' : 'text.secondary' }}>
                      {e.kind === 'api' ? 'request' : e.name}
                      {e.latency_ms ? ` · ${secs(e.latency_ms)}` : ''}
                      {tokens ? ` · Gemini ${(tokens / 1000).toFixed(1)}k tokens` : ''}
                    </Typography>
                  </Box>
                </Stack>
              </React.Fragment>
            );
          })}
          {q.data && q.data.length === 0 && (
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Nothing recorded in this range.</Typography>
          )}
        </>
      )}
    </Drawer>
  );
};

export const Admin: React.FC = () => {
  const theme = useTheme();
  const isAdmin = useIsAdmin();
  const [range, setRange] = useState<Range>('30');
  const [tab, setTab] = useState<AdminTab>('overview');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<AdminUser | null>(null);
  const days = Number(range);
  // A browser tab title that stands out from the site's own tabs while this page is open.
  useEffect(() => {
    const previous = document.title;
    document.title = 'Left Turn Betting [Admin]';
    return () => {
      document.title = previous;
    };
  }, []);
  // Refresh skips the server's 2-minute cache once.
  const fresh = useRef(false);
  const q = useQuery({
    queryKey: ['adminSummary', days],
    queryFn: () => {
      const f = fresh.current;
      fresh.current = false;
      return getAdminSummary(days, f);
    },
    enabled: isAdmin,
    staleTime: 60_000,
  });
  const s = q.data?.summary;
  const emails = q.data?.emails ?? {};
  // The Board scanner tab's saved scans, loaded once the tab is opened.
  const freshScans = useRef(false);
  const scansQ = useQuery({
    queryKey: ['adminBoardScans', days],
    queryFn: () => {
      const f = freshScans.current;
      freshScans.current = false;
      return getAdminBoardScans(days, f);
    },
    enabled: isAdmin && tab === 'scanner',
    staleTime: 60_000,
  });

  const features = useMemo(() => {
    // several raw events can share a label (every "simulate <track>"): keep the largest user count per label
    const byLabel = new Map<string, number>();
    for (const f of s?.features ?? []) {
      const l = featureLabel(f.name, f.detail);
      byLabel.set(l, Math.max(byLabel.get(l) ?? 0, f.users));
    }
    return Array.from(byLabel.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 16);
  }, [s]);

  if (!isAdmin) {
    return (
      <Box sx={{ maxWidth: 1360, mx: 'auto', px: 4, py: 6 }}>
        <Alert severity="info" icon={<Lock />}>
          This page is for site admins.
        </Alert>
      </Box>
    );
  }

  const users = (s?.users ?? []).filter(
    (u) => !search || (u.email ?? u.hash).toLowerCase().includes(search.trim().toLowerCase()),
  );
  const usageUSD = (s?.totals.bigQueryUSD ?? 0) + (s?.totals.geminiUSD ?? 0);

  return (
    <Box sx={{ maxWidth: 1360, mx: 'auto', px: { xs: 2, md: 4 }, py: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5, flexWrap: 'wrap', rowGap: 1 }}>
        <AdminPanelSettings sx={{ color: 'primary.main' }} />
        <Typography variant="h5" sx={{ fontWeight: 800 }}>
          Admin
        </Typography>
        <Chip size="small" icon={<Lock sx={{ fontSize: 14 }} />} label="read:admin" variant="outlined" />
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          variant="outlined"
          startIcon={
            q.isFetching || scansQ.isFetching ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <Refresh fontSize="small" />
            )
          }
          disabled={q.isFetching || scansQ.isFetching}
          onClick={() => {
            fresh.current = true;
            void q.refetch();
            if (tab === 'scanner') {
              freshScans.current = true;
              void scansQ.refetch();
            }
          }}
        >
          Refresh
        </Button>
        <SegmentedToggle
          size="small"
          aria-label="Range"
          value={range}
          onChange={setRange}
          options={[
            { value: '7', label: '7 days' },
            { value: '30', label: '30 days' },
            { value: '90', label: '90 days' },
          ]}
        />
      </Stack>
      <Typography sx={{ color: 'text.secondary', fontSize: 13, mb: 2 }}>
        Read-only. New activity shows up within about 5 minutes. Costs are list prices (BigQuery $6.25/TB billed, Gemini
        per token); Cloud Run and your own bq scripts and backtests aren't included.
        {s ? ` Updated ${ago(s.generatedAt)}.` : ''}
      </Typography>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider', '& .MuiTab-root': { fontWeight: 600, fontSize: 13 } }}
      >
        <Tab value="overview" label="Overview" />
        <Tab value="users" label="Users" />
        <Tab value="costs" label="Costs" />
        <Tab value="activity" label="Activity" />
        <Tab value="scanner" label="Board scanner" />
      </Tabs>

      {q.isLoading && <CircularProgress size={24} />}
      {q.error && <Alert severity="error">{(q.error as Error).message}</Alert>}
      {s && s.activeUsers === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No usage recorded in this range yet. Recording started with this release.
        </Alert>
      )}

      {tab === 'scanner' && (
        <Box>
          {scansQ.isLoading && <CircularProgress size={24} />}
          {scansQ.error && <Alert severity="error">{(scansQ.error as Error).message}</Alert>}
          {scansQ.data && (
            <AdminBoardScanner
              health={scansQ.data.health}
              emails={scansQ.data.emails}
              onUser={(hash) => {
                const u = s?.users.find((x) => x.hash === hash);
                if (u) setSelected(u);
              }}
            />
          )}
        </Box>
      )}

      {s && tab === 'overview' && (
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
            <Tile
              label="ACTIVE USERS"
              value={String(s.activeUsers)}
              note={`${s.activeToday} today · ${s.active7d} in 7 days`}
            />
            <Tile label="ACCOUNTS" value={String(s.accounts)} note={`${s.signups} new in ${days} days`} />
            <Tile label="SIMULATIONS" value={String(s.totals.simulations)} />
            <Tile label="BOARD SCANS" value={String(s.totals.scans)} />
            <Tile label="AI CALLS" value={String(s.totals.aiCalls)} />
            <Tile
              label="COST"
              value={money(usageUSD)}
              note={`${s.activeUsers ? money(usageUSD / s.activeUsers) : '$0'} per active user · upkeep ${money(s.system.bigQueryUSD)}`}
            />
          </Stack>
          <Paper sx={{ p: 2 }}>
            <Typography sx={{ fontWeight: 700, mb: 1 }}>Daily cost</Typography>
            <CostChart daily={s.daily} />
          </Paper>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Paper sx={{ flex: 1.2, p: 2, minWidth: 0, overflowX: 'auto' }}>
              <Typography sx={{ fontWeight: 700 }}>Top users by cost</Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>
                BigQuery and Gemini their requests used
              </Typography>
              <Table size="small">
                <Head cols={['User', 'Simulations', 'Scans', 'AI', 'Cost']} />
                <TableBody>
                  {s.users.slice(0, 8).map((u) => (
                    <TableRow key={u.hash} hover onClick={() => setSelected(u)} sx={{ cursor: 'pointer' }}>
                      <TableCell>
                        {u.email || shortHash(u.hash)}
                        {u.admin && <Chip size="small" label="admin" sx={{ ml: 1, height: 18, fontSize: 10 }} />}
                      </TableCell>
                      <TableCell align="right">{u.simulations}</TableCell>
                      <TableCell align="right">{u.scans}</TableCell>
                      <TableCell align="right">{u.aiCalls}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {money(u.costUSD)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
            <Paper sx={{ flex: 1, p: 2, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, mb: 1 }}>Latest activity</Typography>
              {s.recent.slice(0, 12).map((e, i) => (
                <Stack key={i} direction="row" spacing={1} sx={{ py: 0.75, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography sx={{ fontSize: 13, flex: 1, minWidth: 0 }} noWrap>
                    <b>{(e.user_hash && emails[e.user_hash]) || shortHash(e.user_hash ?? '')}</b> {eventLabel(e)}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary', flex: 'none' }}>
                    {ago(e.event_time)}
                  </Typography>
                </Stack>
              ))}
            </Paper>
          </Stack>
        </Stack>
      )}

      {s && tab === 'users' && (
        <Paper sx={{ overflowX: 'auto' }}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ p: 2, borderBottom: 1, borderColor: 'divider', flexWrap: 'wrap', rowGap: 1 }}
          >
            <TextField
              size="small"
              placeholder="Search by email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ width: 280 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              {s.users.length} active users · emails come from Auth0 when this page loads (usage is stored under hashed
              ids)
            </Typography>
          </Stack>
          <Table size="small">
            <Head
              cols={['User', 'Joined', 'Last seen', 'Sessions', 'Simulations', 'Scans', 'AI calls', 'Cost']}
              leftCols={3}
            />
            <TableBody>
              {users.map((u) => (
                <TableRow
                  key={u.hash}
                  hover
                  selected={selected?.hash === u.hash}
                  onClick={() => setSelected(u)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell sx={{ fontWeight: 600 }}>
                    {u.email || shortHash(u.hash)}
                    {u.admin && <Chip size="small" label="admin" sx={{ ml: 1, height: 18, fontSize: 10 }} />}
                  </TableCell>
                  <TableCell>{u.joined?.slice(0, 10) ?? '–'}</TableCell>
                  <TableCell>{ago(u.lastSeen)}</TableCell>
                  {[u.sessions, u.simulations, u.scans, u.aiCalls].map((v, i) => (
                    <TableCell key={i} align="right">
                      {v}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {money(u.costUSD)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {s && tab === 'costs' && (
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
            <Tile label="BIGQUERY · USERS" value={money(s.totals.bigQueryUSD)} note="their requests' queries" />
            <Tile
              label="BIGQUERY · UPKEEP"
              value={money(s.system.bigQueryUSD)}
              note={`${gb(s.system.bigQueryBytes)}: background work and the studio (not your own scripts)`}
            />
            <Tile
              label="GEMINI"
              value={money(s.totals.geminiUSD)}
              note={`${((s.totals.geminiIn + s.totals.geminiOut) / 1e6).toFixed(2)}M tokens`}
            />
          </Stack>
          <Paper sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <Head cols={['Feature', 'Calls', 'Users', 'BigQuery', 'Gemini', 'Total']} />
              <TableBody>
                {s.routes.map((r) => (
                  <TableRow key={r.route} hover>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {routeLabel(r.route)}
                      <Typography component="span" sx={{ ml: 1, fontSize: 11, color: 'text.secondary' }}>
                        {r.route}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{r.calls.toLocaleString()}</TableCell>
                    <TableCell align="right">{r.users}</TableCell>
                    <TableCell align="right">{money(r.bigQueryUSD)}</TableCell>
                    <TableCell align="right">{r.geminiUSD ? money(r.geminiUSD) : '–'}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {money(r.bigQueryUSD + r.geminiUSD)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Stack>
      )}

      {s && tab === 'activity' && (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Paper sx={{ flex: 1, p: 2 }}>
            <Typography sx={{ fontWeight: 700, mb: 1 }}>What people use</Typography>
            {features.map(([label, n]) => {
              const share = s.activeUsers ? n / s.activeUsers : 0;
              return (
                <Stack key={label} direction="row" alignItems="center" spacing={1.5} sx={{ py: 0.6 }}>
                  <Typography sx={{ width: 220, fontSize: 13, flex: 'none' }} noWrap>
                    {label}
                  </Typography>
                  <Box sx={{ flex: 1, height: 10, borderRadius: 5, bgcolor: alpha(theme.palette.text.primary, 0.08) }}>
                    <Box sx={{ width: `${share * 100}%`, height: '100%', borderRadius: 5, bgcolor: 'primary.main' }} />
                  </Box>
                  <Typography sx={{ width: 64, textAlign: 'right', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
                    {Math.round(share * 100)}% · {n}
                  </Typography>
                </Stack>
              );
            })}
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1 }}>
              Share of active users who used each, last {days} days
            </Typography>
          </Paper>
          <Paper sx={{ flex: 1, p: 2, overflowX: 'auto' }}>
            <Typography sx={{ fontWeight: 700, mb: 1 }}>Speed and errors</Typography>
            <Table size="small">
              <Head cols={['', 'Calls', 'Typical', 'Slow (1 in 20)', 'Errors']} />
              <TableBody>
                {s.loads.map((l) => (
                  <TableRow key={'load' + l.detail}>
                    <TableCell sx={{ fontWeight: 600 }}>{l.detail === 'page' ? 'Page load' : l.detail}</TableCell>
                    <TableCell align="right">{l.n}</TableCell>
                    <TableCell align="right">{secs(l.p50ms)}</TableCell>
                    <TableCell align="right">{secs(l.p95ms)}</TableCell>
                    <TableCell align="right">–</TableCell>
                  </TableRow>
                ))}
                {s.routes
                  .filter((r) => r.calls > 0)
                  .slice(0, 12)
                  .map((r) => {
                    const rate = r.calls ? r.errors / r.calls : 0;
                    return (
                      <TableRow key={r.route}>
                        <TableCell sx={{ fontWeight: 600 }}>{routeLabel(r.route)}</TableCell>
                        <TableCell align="right">{r.calls}</TableCell>
                        <TableCell align="right">{secs(r.p50ms)}</TableCell>
                        <TableCell align="right">{secs(r.p95ms)}</TableCell>
                        <TableCell align="right" sx={{ color: rate > 0.02 ? 'error.main' : undefined }}>
                          {(rate * 100).toFixed(1)}%
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </Paper>
        </Stack>
      )}

      <UserDrawer user={selected} days={days} onClose={() => setSelected(null)} />
    </Box>
  );
};

export default Admin;
